import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { sanityApi } from "@/features/sanity";
import {
  ArrowTopRightIcon,
  ChevronDownIcon,
  Cross1Icon,
  HamburgerMenuIcon,
} from "@radix-ui/react-icons";
import SideDropdown from "@/components/design/SideDropdown";
import { AnimatePresence, motion } from "framer-motion";
import {
  IconBook,
  IconHome,
  IconHomeMove,
  IconHomeSearch,
  IconHomeStats,
  IconPhone,
  IconUsersGroup,
} from "@tabler/icons-react";
import { useFormStarted, useCompletedSteps } from "@/stores";
import { Badge } from "@/components/ui/badge";

export default function NavBar({ className }) {
  const navigate = useNavigate();
  const [dropdown, setDropdown] = useState("");
  const [navbarPosts, setNavbarPosts] = useState([]); // State to hold Ratgeber posts
  const [navbarVerkaufenTipps, setNavbarVerkaufenTipps] = useState([]); // State to hold Ratgeber posts
  const [navbarKaufenTipps, setNavbarKaufenTipps] = useState([]); // State to hold Ratgeber posts
  const [navbarVermietenTipps, setNavbarVermietenTipps] = useState([]); // State to hold Ratgeber posts
  const [cityPages, setCityPages] = useState([]); // State to hold city pages
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Mobile menu state
  const [openSubMenu, setOpenSubMenu] = useState(null); // Tracks which sub-menu is open

  // Use new store hooks
  const started = useFormStarted();
  const completedSteps = useCompletedSteps();
  const isCompleted = completedSteps.includes("contact");
  const showResumeBadge = started && !isCompleted;

  // Add these state variables at the top of your component
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);

  // This effect listens to scroll events to hide/show the navbar
  useEffect(() => {
    const handleScroll = () => {
      // Don't hide navbar if dropdown or mobile menu is open
      if (dropdown || isMobileMenuOpen) {
        return;
      }

      const currentScrollPos = window.pageYOffset;
      // If scrolling down (and you've scrolled at least 100px), hide the navbar; otherwise, show it
      if (currentScrollPos > prevScrollPos && currentScrollPos > 100) {
        setVisible(false);
      } else {
        setVisible(true);
      }
      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScrollPos, dropdown, isMobileMenuOpen]);

  // Fetch dynamic posts for the Ratgeber section
  useEffect(() => {
    async function loadNavbarPosts() {
      const data = await sanityApi.fetchNavbarBeitraege();
      setNavbarPosts(data);
    }
    async function loadNavbarVerkaufenTipps() {
      const data = await sanityApi.fetchNavbarVerkaufenTipps();
      const sanitizedData = data[0]?.slug?.current; // Added optional chaining

      setNavbarVerkaufenTipps(sanitizedData);
    }
    async function loadNavbarKaufenTipps() {
      const data = await sanityApi.fetchNavbarKaufenTipps();
      const sanitizedData = data[0]?.slug?.current; // Added optional chaining

      setNavbarKaufenTipps(sanitizedData);
    }
    async function loadNavbarVermietenTipps() {
      const data = await sanityApi.fetchNavbarVermietenTipps();
      const sanitizedData = data[0]?.slug?.current; // Added optional chaining
      setNavbarVermietenTipps(sanitizedData);
    }
    async function loadCityPages() {
      try {
        const data = await sanityApi.fetchFooterCityPages();
        setCityPages(data);
      } catch (error) {
        console.error("Failed to load city pages for navbar", error);
      }
    }
    loadNavbarPosts();
    loadNavbarVerkaufenTipps();
    loadNavbarKaufenTipps();
    loadNavbarVermietenTipps();
    loadCityPages();
  }, []);

  const handleMouseEnter = (menu) => setDropdown(menu);
  const handleMouseLeave = () => setDropdown("");

  const handleLinkClick = (navigateTo) => {
    setDropdown("");
    navigate(navigateTo);
    setIsMobileMenuOpen(false); // Close the mobile menu on navigation
  };
  const toggleSubMenu = (index) => {
    setOpenSubMenu((prev) => (prev === index ? null : index));
  };

  const dropdownVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0 },
    exit: {
      opacity: 0,
      y: 0,
      transition: {
        duration: 0.1,
        ease: "easeOut",
      },
    },
  };

  // Your existing navItems array
  const navItems = [
    {
      title: "Bewerten",
      links: [
        { name: "Immobilie bewerten", path: "/bewerten/immobilie" },
        { name: "Haus bewerten", path: "/bewerten/haus" },
        { name: "Wohnung bewerten", path: "/bewerten/wohnung" },
        {
          name: "Mehrfamilienhaus bewerten",
          path: "/bewerten/mehrfamilienhaus",
        },
      ],
      navigate: "/bewerten",
      details: (
        <div className="p-5 bg-prime-50 rounded-md w-full  h-full">
          <IconHomeStats className="h-7 w-7 text-prime-950/70 " />
          <h3 className="text-lg text-prime-950 font-medium">
            Ihre kostenlose Immobilienbewertung{" "}
          </h3>
          <p className="text-sm  text-gray-600">
            Ermittelten Sie schnell und unverbindlich den Marktwert Ihrer
            Immobilie.{" "}
          </p>
          <Link className="relative" to="/bewerten/immobilie/online-bewertung">
            {showResumeBadge && (
              <Badge className="absolute -top-3 -right-3  bg-orange-600/90 border border-orange-600 rounded-full font-light text-xs">
                Fortsetzen
              </Badge>
            )}
            <button className="mt-4 text-[16px] font-medium group/nav-card text-dark-200 w-full p-2 bg-gradient-to-r from-prime-100 to-prime-200 rounded-lg transition-all hover:shadow-lg hover:bg-gray-200  stroke-white text-pretty flex gap-2 justify-center items-center">
              Kostenlose Immobilienbewertung{" "}
              <ArrowTopRightIcon className="stroke-prime-950 rotate-45 group-hover/nav-card:-rotate-0 transition-transform" />
            </button>
          </Link>
        </div>
      ),
    },
    {
      title: "Verkaufen",
      links: [
        { name: "Immobilie verkaufen", path: "/verkaufen/immobilie" },
        { name: "Beratungstermin", path: "/beratungstermin" },
        { name: "Referenzen", path: "/referenzen" },
        {
          name: "Tipps zum Immobilienverkauf",
          path: `/ratgeber/${navbarVerkaufenTipps}`,
        },
      ],
      navigate: "/verkaufen",
      details: (
        <div className="p-5 bg-prime-50 rounded-md w-full  h-full">
          <IconHome className="h-7 w-7 text-prime-950/70 " />
          <h3 className="text-lg text-prime-950 font-medium">
            {" "}
            Immobilienverkauf mit Herz und Verstand
          </h3>
          <p className="text-sm  text-gray-600">
            Mit Erfahrung, Feingefühl und regionaler Expertise begleiten wir Sie
            persönlich beim Verkauf Ihrer Immobilie.
          </p>
          <button
            onClick={() => navigate("/bewerten/immobilie/online-bewertung")}
            className="mt-4 text-[16px] font-medium group/nav-card text-dark-200 w-full p-2 bg-gradient-to-r from-prime-100 to-prime-200 rounded-lg transition-all hover:shadow-lg hover:bg-gray-200  stroke-white text-pretty flex gap-2 justify-center items-center"
          >
            Kostenlose Immobilienbewertung{" "}
            <ArrowTopRightIcon className="stroke-prime-950 rotate-45 group-hover/nav-card:-rotate-0 transition-transform" />
          </button>
        </div>
      ),
    },
    {
      title: "Vermieten",
      links: [
        { name: "Immobilie vermieten", path: "/vermieten" },
        {
          name: "Tipps zur Vermietung",
          path: `/ratgeber/${navbarVermietenTipps}`,
        },
      ],
      navigate: "/vermieten",
      details: (
        <div className="p-5 bg-prime-50 rounded-md w-full  h-full">
          <IconHomeMove className="h-7 w-7 text-prime-950/70 " />
          <h3 className="text-lg text-prime-950 font-medium">
            Sichere Immobilienvermietung
          </h3>
          <p className="text-sm  text-gray-600">
            Wir beraten Sie kostenlos und unverbindlich zur Vermietung Ihrer
            Immobilie.
          </p>
          <Link to={"/vermieten"}>
            <button className="mt-4 text-[16px] font-medium group/nav-card text-dark-200 w-full p-2 bg-gradient-to-r from-prime-100 to-prime-200 rounded-lg transition-all hover:shadow-lg hover:bg-gray-200  stroke-white text-pretty flex gap-2 justify-center items-center">
              Mehr erfahren{" "}
              <ArrowTopRightIcon className="stroke-prime-950 rotate-45 group-hover/nav-card:-rotate-0 transition-transform" />
            </button>
          </Link>
        </div>
      ),
    },
    {
      title: "Immobilienangebot",
      links: [
        {
          name: "Immobilie zum Kauf",
          path: "/immobilien?minPrice=0&maxPrice=1000000&minBedrooms=0&maxBedrooms=20&minBathrooms=0&maxBathrooms=15&minLotSize=0&maxLotSize=10000&minLivingSpace=0&maxLivingSpace=50000&minBuildYear=1900&maxBuildYear=2025&offer_type=BUY",
        },
        {
          name: "Immobilie zur Miete",
          path: "/immobilien?minPrice=0&maxPrice=1000000&minBedrooms=0&maxBedrooms=20&minBathrooms=0&maxBathrooms=15&minLotSize=0&maxLotSize=10000&minLivingSpace=0&maxLivingSpace=50000&minBuildYear=1900&maxBuildYear=2025&offer_type=RENT",
        },
        { name: "Suchprofil anlegen", path: "/immobilien/suchprofil-anlegen" },
        {
          name: "Tipps zum Immobilienkauf",
          path: `/ratgeber/${navbarKaufenTipps}`,
        },
      ],
      navigate: "/immobilien",
      details: (
        <div className="p-5 bg-prime-50 rounded-md w-full  h-full">
          <IconHomeSearch className="h-7 w-7 text-prime-950/70 " />
          <h3 className="text-lg text-prime-950 font-medium">
            Finden Sie Ihre Traumimmobilie
          </h3>
          <p className="text-sm  text-gray-600">
            Viele unserer Immobilien gehen nur an vorgemerkte Suchkunden.
            Erstellen Sie jetzt Ihr Suchprofil und sichern Sie sich exklusive
            Angebote!
          </p>
          <Link to={"/immobilien/suchprofil-anlegen"}>
            <button className="mt-4 text-[16px] font-medium group/nav-card text-dark-200 w-full p-2 bg-gradient-to-r from-prime-100 to-prime-200 rounded-lg transition-all hover:shadow-lg hover:bg-gray-200  stroke-white text-pretty flex gap-2 justify-center items-center">
              Suchprofil erstellen{" "}
              <ArrowTopRightIcon className="stroke-prime-950 rotate-45 group-hover/nav-card:-rotate-0 transition-transform inline-block" />
            </button>
          </Link>
        </div>
      ),
    },
    {
      title: "Unternehmen",
      links: [
        { name: "Über uns", path: "/unternehmen/über-uns" },
        // { name: "Standort", path: "/unternehmen/standort" },
        { name: "Kontakt", path: "/unternehmen/kontakt" },
        // City pages will be dynamically added here
      ],
      navigate: "/unternehmen",
      details: (
        <div className="p-5 bg-prime-50 rounded-md w-full  h-full">
          <IconUsersGroup className="h-7 w-7 text-prime-950/70 " />
          <h3 className="text-lg text-prime-950 font-medium">
            Vertrauen über Generationen
          </h3>
          <p className="text-sm  text-gray-600">
            Als Familienunternehmen in dritter Generation verbinden wir
            regionale Marktkenntnis mit echter Leidenschaft für Immobilien
          </p>
          <Link to={"/unternehmen/über-uns"}>
            {" "}
            <button className="mt-4 text-[16px] font-medium group/nav-card text-dark-200 w-full p-2 bg-gradient-to-r from-prime-100 to-prime-200 rounded-lg transition-all hover:shadow-lg hover:bg-gray-200  stroke-white text-pretty flex gap-2 justify-center items-center">
              Mehr erfahren{" "}
              <ArrowTopRightIcon className="stroke-prime-950 rotate-45 group-hover/nav-card:-rotate-0 transition-transform" />
            </button>
          </Link>
        </div>
      ),
    },
    {
      title: "Ratgeber",
      links: [],
      navigate: "/ratgeber",
      details: (
        <div className="p-5 bg-prime-50 rounded-md w-full  h-full">
          <IconBook className="h-7 w-7 text-prime-950/70 " />
          <h3 className="text-lg text-prime-950 font-medium">
            Gut beraten. Besser entscheiden.
          </h3>
          <p className="text-sm  text-gray-600">
            Ob Verkauf, Erbe oder Immobilienwert – wir teilen unser Wissen,
            damit Sie klare Entscheidungen treffen können.
          </p>
          <button
            onClick={() => navigate("/ratgeber")}
            className="mt-4 text-[16px] font-medium group/nav-card text-dark-200 w-full p-2 bg-gradient-to-r from-prime-100 to-prime-200 rounded-lg transition-all hover:shadow-lg hover:bg-gray-200  stroke-white text-pretty flex gap-2 justify-center items-center"
          >
            Zu den Ratgebern{" "}
            <ArrowTopRightIcon className="stroke-prime-950 rotate-45 group-hover/nav-card:-rotate-0 transition-transform" />
          </button>
        </div>
      ),
    },
  ];

  // Dynamically inject fetched Ratgeber posts
  const ratgeberIndex = navItems.findIndex((item) => item.title === "Ratgeber");
  if (ratgeberIndex !== -1) {
    const dynamicLinks = navbarPosts.map((post) => ({
      name: post.title,
      path: `/ratgeber/${post.slug.current}`,
    }));
    navItems[ratgeberIndex].links = [
      ...navItems[ratgeberIndex].links,
      ...dynamicLinks,
    ];
  }

  // Dynamically inject city pages into Unternehmen section
  const unternehmenIndex = navItems.findIndex(
    (item) => item.title === "Unternehmen"
  );
  if (unternehmenIndex !== -1 && cityPages.length > 0) {
    const cityLinks = cityPages.map((page) => ({
      name: `Immobilienmakler in ${page.title}`,
      path: `/${page.slug.current}`,
    }));
    navItems[unternehmenIndex].links = [
      ...navItems[unternehmenIndex].links,
      ...cityLinks,
    ];
  }

  return (
    <motion.div
      initial={{ top: 0 }}
      animate={{ top: visible ? "0" : "-100px" }}
      transition={{ duration: 0.3 }}
      className={cn(
        "py-1 md:py-2 shadow-sm fixed left-0 right-0 bg-white bg-opacity-20 backdrop-blur-lg z-40",
        className
      )}
    >
      <div className="max-w-[95rem] mx-auto flex justify-between items-center py-2 px-6">
        <div className="flex items-center border">
          <Link to="/">
            <img
              src="/jakobs_logo.png"
              alt="Immobilienbüro Logo"
              className="md:h-[3rem] h-[2.5rem] w-auto cursor-pointer hover:shadow-xl transition-all duration-500"
            />
          </Link>
        </div>

        <div className="justify-evenly px-10  w-full flex-1  font-sans text-lg text-gray-700 lg:flex   hidden  max-w-7xl">
          {navItems.map((item, index) => (
            <div key={index} className="group h-full">
              <Link
                to={`${item.navigate}`}
                onMouseEnter={() => handleMouseEnter(item.title)}
                className="cursor-pointer text-prime-950 relative py-7 group-hover:text-prime-800"
              >
                {item.title}
                <span className="block max-w-0 group-hover:max-w-full transition-all duration-300 h-0.5 bg-prime-800"></span>
              </Link>
              <AnimatePresence>
                {dropdown === item.title && (
                  <motion.div
                    onClick={handleMouseLeave}
                    className="h-[calc(100vh-4rem)] absolute top-[5rem] left-0 right-0 bg-gray-600/40 overflow-y-auto"
                  >
                    <motion.div
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      variants={dropdownVariants}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="dropdown bg-white overflow-hidden absolute top-[0.5px] left-0 right-0 py-2 pb-5"
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setDropdown(item.title);
                      }}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="max-w-4xl mx-auto flex gap-5 justify-between">
                        <div className="flex flex-col gap-2 w-1/2">
                          {item.links.map((link, idx) => (
                            <Link
                              to={link.path}
                              key={idx}
                              className="hover:text-prime-800 font-medium text-gray-700 cursor-pointer"
                            >
                              {link.name}
                            </Link>
                          ))}
                        </div>
                        <div className="bg-prime-50 rounded-lg  flex-1 h-full">
                          {item.details}
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-5">
          <div className="cursor-pointer translate-y-1 relative">
            <SideDropdown />
          </div>
          <div className="lg:hidden flex items-center">
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="relative flex items-center"
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ opacity: 0, rotate: -90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: 90 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Cross1Icon className="h-6 w-6 text-prime-950" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="open"
                    initial={{ opacity: 0, rotate: 90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: -90 }}
                    transition={{ duration: 0.3 }}
                  >
                    <HamburgerMenuIcon className="h-6 w-6 text-prime-950" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
          <div className="hidden  relative w-fit self-end   xl:flex items-center space-x-2 bg-gradient-to-br from-prime-800 to-prime-950 p-2 text-white rounded-sm">
            <IconPhone className="size-5 stroke-1" />
            <p className="text-primary-foreground font-light">
              <a href="tel:+49 2403 883170">+49 2403 883170</a>{" "}
            </p>
            <span className="absolute -top-1 -left-3 flex h-3 w-3">
              <span className="animate-ping delay-1000 absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-lime-500"></span>
            </span>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      <AnimatePresence mode="wait">
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "calc(100vh - 65px)" }}
            exit={{ opacity: 0, height: 0, transition: { duration: 0.3 } }}
            className="fixed top-[65px]  left-0 w-full bg-white z-50 flex flex-col"
          >
            {/* Mobile Menu Items Container */}
            <div className="flex-grow overflow-y-auto p-4">
              {/* (Place snippet 3 here) */}
              {navItems.map((item, index) => (
                <div key={index} className="mb-4 border-b pb-4">
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => toggleSubMenu(index)}
                  >
                    <a
                      onClick={() => handleLinkClick(item[index].path)}
                      className="text-lg font-medium"
                    >
                      {item.title}
                    </a>
                    {item.links && item.links.length > 0 && (
                      <ChevronDownIcon
                        className={`w-5 h-5 transition-transform stroke-gray-400 ${
                          openSubMenu === index ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    )}
                  </div>

                  <AnimatePresence mode="wait">
                    {openSubMenu === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 1 }}
                        animate={{
                          height: "auto",
                          opacity: 1,
                          transition: { duration: 0.4 },
                        }}
                        exit={{
                          height: 0,
                          opacity: 0,
                          transition: { duration: 0.4 },
                        }}
                        className="pl-4 mt-2 overflow-hidden"
                      >
                        {item.links.map((link, idx) => (
                          <a
                            key={idx}
                            onClick={() => handleLinkClick(link.path)}
                            className="block py-2 text-gray-600 hover:text-prime-800"
                          >
                            {link.name}
                          </a>
                        ))}
                        {/* Render the details CTA for this nav item */}
                        <div className="mt-4">{item.details}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
            <div className="p-2 m-2 w-1/2 rounded-lg border-t flex items-center space-x-2 bg-gradient-to-br from-prime-800 to-prime-950 text-white">
              <IconPhone className="size-5 stroke-1" />
              <p className="text-primary-foreground font-light">
                <a href="tel:+49 2403 883170">+49 2403 883170</a>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
